import React, { useCallback, useEffect, useState } from 'react';
import { getMainAxios } from '../utils/util';
import { useFdsNav } from '../context/FdsNavContext';
import ResearchDocViewer from './ResearchDocViewer';

interface SystemCard {
  id: string;
  title: string;
  tagline?: string;
  baseline?: string;
  livePage?: string;
  liveLabel?: string;
}

interface ManifestRes {
  systems?: SystemCard[];
}

interface DocRes {
  id: string;
  content: string;
  updatedUtc?: string;
}

export default function ResearchSystems() {
  const navigate = useFdsNav();
  const [systems, setSystems] = useState<SystemCard[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [doc, setDoc] = useState<DocRes | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const axios = getMainAxios();
        const res = await axios.get<ManifestRes>('/api/v1/research/systems');
        const list = res.data?.systems ?? [];
        setSystems(list);
        if (list.length > 0) setSelectedId(list[0].id);
      } catch (e: unknown) {
        setError(String(e));
      }
    })();
  }, []);

  const loadDoc = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const axios = getMainAxios();
      const res = await axios.get<DocRes>(`/api/v1/research/systems/${id}`);
      setDoc(res.data);
    } catch (e: unknown) {
      setError(String(e));
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) loadDoc(selectedId);
  }, [selectedId, loadDoc]);

  const selected = systems.find(s => s.id === selectedId);

  return (
    <div style={{ padding: 16, textAlign: 'left' }}>
      <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Systems</h2>
      <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: 13 }}>
        운영 시스템 카탈로그 — 역할·근거·Live(Quant) 연결
      </p>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {systems.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSelectedId(s.id)}
              style={{
                textAlign: 'left',
                padding: '12px 14px',
                borderRadius: 8,
                border: selectedId === s.id ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                background: selectedId === s.id ? '#eff6ff' : '#fff',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13 }}>{s.title}</div>
              {s.tagline && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{s.tagline}</div>}
              {s.baseline && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>baseline {s.baseline}</div>}
            </button>
          ))}
        </div>

        <div style={{ flex: '1 1 400px', minWidth: 320 }}>
          {selected?.livePage && (
            <button
              type="button"
              onClick={() => navigate(selected.livePage!)}
              style={{
                marginBottom: 12,
                padding: '6px 12px',
                fontSize: 12,
                background: '#1e293b',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {selected.liveLabel ?? 'Live ↗'}
            </button>
          )}
          {error && <p style={{ color: '#dc2626', fontSize: 12 }}>{error}</p>}
          {loading && <p style={{ color: '#64748b' }}>로딩…</p>}
          {!loading && doc?.content && <ResearchDocViewer content={doc.content} />}
          {!loading && !doc?.content && selectedId && (
            <p style={{ color: '#94a3b8' }}>문서 없음 — docs/research/systems/{selectedId}.md</p>
          )}
        </div>
      </div>
    </div>
  );
}
